import React, { useEffect, useState, useCallback } from "react";
import {
  Card, CardHeader, CardBody, Container, Row, Col, Button, Table, Badge, Input,
} from "reactstrap";
import axiosInstance from "../Request";
import { URL } from "../api";

const StatusBadge = ({ value }) => {
  const v = String(value || "PENDING").toUpperCase();
  const color = v === "SUCCESS" ? "success" : v === "FAILED" ? "danger" : "secondary";
  return <Badge color={color} pill>{v}</Badge>;
};

const EnrichmentRetry = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const [looking, setLooking] = useState(false);

  // Client-side filter by customer id (also matches name / mobile / PAN for convenience).
  const q = search.trim().toLowerCase();
  const filteredRows = q
    ? rows.filter((r) =>
        String(r.customerId ?? "").includes(q) ||
        String(r.borrowerName ?? "").toLowerCase().includes(q) ||
        String(r.mobile ?? "").includes(q) ||
        String(r.pan ?? "").toLowerCase().includes(q))
    : rows;

  const load = useCallback(async () => {
    setLoading(true);
    setSelected([]);
    setMsg("");
    try {
      const res = await axiosInstance.get(URL.PAYMENT_ENRICHMENT_CANDIDATES, { params: { limit: 500 } });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Load candidates failed:", e);
      setRows([]);
      setMsg("Could not load candidates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetch a single customer by id from the backend and merge into the list — lets you find
  // any customer, not just the capped 500 loaded above.
  const lookupServer = async () => {
    const id = q.replace(/\D/g, "");
    if (!id) return;
    setLooking(true);
    setMsg("");
    try {
      const res = await axiosInstance.get(`${URL.PAYMENT_ENRICHMENT_CANDIDATE}/${id}`);
      const row = res.data;
      if (row && row.customerId != null) {
        setRows((rs) => (rs.some((r) => r.customerId === row.customerId) ? rs : [row, ...rs]));
        setSearch(String(row.customerId));
      } else {
        setMsg(`Customer ${id} not found.`);
      }
    } catch (e) {
      setMsg(e?.response?.status === 404 ? `Customer ${id} not found.` : "Lookup failed.");
    } finally {
      setLooking(false);
    }
  };

  const toggleRow = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.includes(r.customerId));
  const toggleAll = () => setSelected(allSelected ? [] : filteredRows.map((r) => r.customerId));

  const run = async (customerIds, label) => {
    if (!customerIds.length) { alert("Nothing selected."); return; }
    if (!window.confirm(`Re-run prefill / PAN / CKYC for ${label} (${customerIds.length})? Updates in place — no duplicates.`))
      return;
    setRunning(true);
    setMsg("");
    try {
      const res = await axiosInstance.post(URL.PAYMENT_RETRY_ENRICHMENT, { customerIds });
      const d = res.data || {};
      setMsg(`Processed ${d.processed ?? 0}/${customerIds.length} · Prefill OK ${d.prefillSuccess ?? 0} · PAN OK ${d.panSuccess ?? 0} · CKYC OK ${d.ckycSuccess ?? 0}`);
      await load();
    } catch (e) {
      console.error("Retry failed:", e);
      setMsg("Retry failed.");
    } finally {
      setRunning(false);
    }
  };

  const runSelected = () => run(selected, "selected");
  const runAll = () => run(rows.map((r) => r.customerId), "all listed");

  // Run a single API (prefill | pan | ckyc) for one customer and update just that cell.
  const [runningCell, setRunningCell] = useState("");
  const runOne = async (customerId, api) => {
    setRunningCell(`${customerId}:${api}`);
    try {
      const res = await axiosInstance.post(`${URL.PAYMENT_RETRY_API}/${customerId}`, null, { params: { api } });
      const status = res.data?.status || "FAILED";
      const key = api === "ckyc" ? "ckycStatus" : api === "pan" ? "panStatus" : api === "penny" ? "pennyStatus" : "prefillStatus";
      setRows((rs) => rs.map((r) => (r.customerId === customerId ? { ...r, [key]: status } : r)));
    } catch (e) {
      console.error(`Retry ${api} failed:`, e);
      alert(`Retry ${api.toUpperCase()} failed for customer ${customerId}.`);
    } finally {
      setRunningCell("");
    }
  };

  const Cell = ({ id, api, value }) => (
    <div className="d-flex align-items-center" style={{ gap: "6px" }}>
      <StatusBadge value={value} />
      <Button color="link" size="sm" className="p-0" title={`Run ${api.toUpperCase()}`}
        disabled={runningCell === `${id}:${api}`} onClick={() => runOne(id, api)}>
        {runningCell === `${id}:${api}` ? "…" : "▶ Run"}
      </Button>
    </div>
  );

  return (
    <Container className="mt--7" fluid>
      <Card className="bg-gradient-default shadow mb-4">
        <CardBody>
          <h6 className="text-uppercase text-light ls-1 mb-1">NBFC · Onboarding APIs</h6>
          <h2 className="text-white mb-0">Enrichment Retry (Prefill / PAN / CKYC)</h2>
        </CardBody>
      </Card>

      <Card className="shadow">
        <CardHeader className="border-0 d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
          <h3 className="mb-0 mr-3">Customers needing enrichment</h3>
          <Button color="primary" size="sm" onClick={load} disabled={loading || running}>
            {loading ? "Loading…" : "Reload"}
          </Button>
          <Button color="success" size="sm" onClick={runSelected} disabled={running || !selected.length}>
            {running ? "Running…" : `Retry Selected (${selected.length})`}
          </Button>
          <Button color="warning" size="sm" onClick={runAll} disabled={running || !rows.length}>
            {running ? "Running…" : "Retry All Listed"}
          </Button>
          <Input type="search" bsSize="sm" placeholder="Search customer id…" style={{ maxWidth: "200px" }}
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && filteredRows.length === 0) lookupServer(); }} />
          {q && filteredRows.length === 0 && /\d/.test(q) && (
            <Button color="info" size="sm" onClick={lookupServer} disabled={looking}>
              {looking ? "Looking…" : `Look up #${q.replace(/\D/g, "")} on server`}
            </Button>
          )}
          {!loading && (
            <Badge color="secondary" pill className="ml-2">
              {q ? `${filteredRows.length} of ${rows.length}` : `${rows.length}`} rows
            </Badge>
          )}
          {msg && <span className="text-muted small ml-2">{msg}</span>}
        </CardHeader>

        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light">
            <tr>
              <th><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all" /></th>
              <th>Customer ID</th><th>Name</th><th>Mobile</th><th>PAN</th>
              <th>Prefill</th><th>PAN API</th><th>CKYC</th><th>Penny Drop</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan="9" className="text-center text-muted py-4">
                {loading ? "Loading…" : q ? "Not in the loaded list — use “Look up on server” above to fetch this customer." : "No customers need enrichment 🎉"}
              </td></tr>
            ) : (
              filteredRows.map((r) => (
                <tr key={r.customerId}>
                  <td><input type="checkbox" checked={selected.includes(r.customerId)} onChange={() => toggleRow(r.customerId)} /></td>
                  <td>{r.customerId}</td>
                  <td>{r.borrowerName || "—"}</td>
                  <td>{r.mobile || "—"}</td>
                  <td>{r.pan || "—"}</td>
                  <td><Cell id={r.customerId} api="prefill" value={r.prefillStatus} /></td>
                  <td><Cell id={r.customerId} api="pan" value={r.panStatus} /></td>
                  <td><Cell id={r.customerId} api="ckyc" value={r.ckycStatus} /></td>
                  <td><Cell id={r.customerId} api="penny" value={r.pennyStatus} /></td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
};

export default EnrichmentRetry;
